/**
 * The one deadline primitive in the client (R1-1, R1-3).
 *
 * **Why it exists at all.** Nothing in this app had a timeout. `fetch` has no default one,
 * `indexedDB.open()` fires no event when the browser simply stops answering, and an
 * IndexedDB transaction suspended with an iOS tab may never fire any of its four events. Each
 * of those is the same shape — *an `await` with no timeout, no catch, and nothing to catch
 * it* — and each renders as the app being slow rather than as the app being broken.
 *
 * **One primitive, two shapes of answer.** `settle()` resolves to {@link TIMED_OUT} rather
 * than rejecting, because the two callers want opposite things from the same event: the
 * transport turns it into a `NetworkError`, and the device-cache boot turns it into the
 * *existing* memory-store degrade. A primitive that rejected would force the store's caller to
 * catch — and a `catch` there is what would swallow `openIndexedDbStore()`'s own reject path,
 * which is the one thing R1-3 may not touch.
 *
 * **A rejection from `work` is never absorbed — until the deadline has fired.** Before it,
 * `Promise.race` propagates a rejection unchanged, so every existing failure path keeps its
 * existing error object and its existing type test. **After it, a rejection IS the deadline**,
 * and `settle()` answers {@link TIMED_OUT} for it. That qualification was paid for: `deadline()`
 * aborts its signal and then resolves `expired`, and in Chromium an aborted `fetch` rejects
 * **synchronously inside `abort()`** — so the race saw the `AbortError` first, `fetchTransport`
 * wrapped it as a plain `NetworkError`, and a request that ran the full twenty seconds was read
 * everywhere above as *never sent* (D189(b) inverted: the web leaf's PEC-8 offer became an
 * automatic second dispatch wherever the activation was still live, the Android container ran
 * its fallback with D61's expiry check off, and D272's F1 told the resident to check a signal
 * that was working). Node's `fetch` rejects a tick later and hid it; the transport's own test
 * used a double that ignored the signal and hid it too. Ordering the two calls the other way
 * round would also pass, by reaction order alone — the flag is what makes the answer not depend
 * on which engine's `fetch` rejects first.
 *
 * `signal` is offered beside it so a caller can *cancel* rather than merely stop waiting.
 * `AbortSignal.timeout()` (which `packages/functions/state/hebcal.ts` uses server-side) would
 * do the abort half and nothing else: the promise it aborts still has to settle, and a `fetch`
 * polyfill or a test double that ignores its signal would hang forever behind it. One
 * controller and one timer here cover both halves and cannot disagree about when the deadline
 * passed.
 */

/**
 * 🔸 **Assumption, not a decision.** No `D-x` fixes this number. 20 s is longer than the
 * slowest authenticated path's measured worst case — P4's open budget is bounded by API
 * Gateway's 30 s integration timeout, and D129's queue window answers `409 gate-busy` or `504`
 * inside it — and short enough that a resident on a dead cellular link learns something. It is
 * one exported constant rather than a literal at a call site precisely so that changing it is
 * one edit and one review.
 */
export const REQUEST_TIMEOUT_MS = 20_000

/** What `settle()` resolves to when the deadline won. A symbol, so no `T` can collide with it. */
export const TIMED_OUT: unique symbol = Symbol('deadline-expired')

export interface Deadline {
  /**
   * Aborted when the deadline passes. Hand it to `fetch` so the request is *cancelled* rather
   * than abandoned — an abandoned request still holds a socket and still delivers a body.
   */
  readonly signal: AbortSignal
  /**
   * `work`'s value, or {@link TIMED_OUT}. Reusable: a caller with two awaits under one budget
   * (send, then read the body) passes each through and shares the single deadline.
   */
  settle<T>(work: Promise<T>): Promise<T | typeof TIMED_OUT>
  /** Stop the timer. Always in a `finally` — an uncleared timer keeps a test runner awake. */
  cancel(): void
}

export function deadline(timeoutMs: number): Deadline {
  const controller = new AbortController()
  let expire: () => void = () => undefined
  // Assigned by the executor below, which runs synchronously — so it is in place before any
  // timer callback can reach it.
  const expired = new Promise<typeof TIMED_OUT>((resolve) => {
    expire = () => resolve(TIMED_OUT)
  })
  // Set before the abort, so a `fetch` that rejects synchronously inside `abort()` already reads
  // it. This flag, not the order of the two calls below, is what decides the answer.
  let fired = false
  const timer = setTimeout(() => {
    fired = true
    // Abort first: the point of the signal is that the work stops, not merely that we stop
    // waiting for it.
    controller.abort()
    expire()
  }, timeoutMs)

  return {
    signal: controller.signal,
    settle: <T>(work: Promise<T>): Promise<T | typeof TIMED_OUT> =>
      Promise.race<T | typeof TIMED_OUT>([
        // A rejection that arrives after the deadline fired is the cancellation we asked for,
        // whatever the engine names it; one that arrives before it is the work's own and passes
        // through untouched.
        work.then(undefined, (error: unknown) => {
          if (fired) return TIMED_OUT
          throw error
        }),
        expired,
      ]),
    cancel: () => clearTimeout(timer),
  }
}
